import { useState, useEffect, useRef, useCallback } from 'react';
import { createAthenaVoice } from '../voice/AthenaVoice';

export const IS_MOBILE = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  || (navigator.maxTouchPoints > 1 && window.innerWidth < 1024);

/**
 * Bridge React ↔ VoiceEngine.
 *
 * @param {object} opts
 * @param {'home'|'workspace'|'visual-call'} opts.context — determina o mapa de estados visuais
 * @param {boolean} opts.initialContinuousMode      — continuousMode inicial
 * @param {(text: string) => void} opts.onDispatch  — texto finalizado pronto para envio
 * @param {(command: string) => void} opts.onWakeWord — wake word detectada (modo passivo)
 * @param {(partial: string) => void} opts.onPartialResult — interim para atualizar UI em tempo real
 * @param {(failure: object) => void} opts.onCaptureFailure — diagnóstico de captura sem texto
 */
export function useVoiceEngine({
  context              = 'home',
  initialContinuousMode = false,
  onDispatch,
  onWakeWord,
  onPartialResult,
  onCaptureFailure,
} = {}) {
  const initialState = context === 'workspace' ? 'idle' : 'passive';
  const [voiceState,     setVoiceState]     = useState(initialState);
  const [continuousMode, setContinuousModeState] = useState(initialContinuousMode);
  const [voiceDiagnostics, setVoiceDiagnostics] = useState(null);
  const [voiceLevel, setVoiceLevel] = useState(0);

  const engineRef          = useRef(null);
  const onDispatchRef      = useRef(onDispatch);
  const onWakeWordRef      = useRef(onWakeWord);
  const onPartialResultRef = useRef(onPartialResult);
  const onCaptureFailureRef = useRef(onCaptureFailure);

  // Mantém refs atualizados sem recriar o engine
  useEffect(() => { onDispatchRef.current      = onDispatch;      }, [onDispatch]);
  useEffect(() => { onWakeWordRef.current       = onWakeWord;      }, [onWakeWord]);
  useEffect(() => { onPartialResultRef.current  = onPartialResult; }, [onPartialResult]);
  useEffect(() => { onCaptureFailureRef.current = onCaptureFailure; }, [onCaptureFailure]);

  // Engine criado uma única vez — sem rerender triggers
  useEffect(() => {
    // A UI usa o mesmo contrato em todas as superfícies. A fachada seleciona
    // WebVoiceProvider no site/PWA e NativeVoiceProvider no Capacitor Android.
    let engine;
    engine = createAthenaVoice({
      context,
      continuousMode: initialContinuousMode,
      isMobile: IS_MOBILE,
      onStateChange:   (vs) => setVoiceState(vs),
      onDispatch:      (t)  => onDispatchRef.current?.(t),
      onWakeWord:      (c)  => onWakeWordRef.current?.(c),
      onPartialResult: (p)  => onPartialResultRef.current?.(p),
      onCaptureFailure: (f) => onCaptureFailureRef.current?.(f),
      onVoiceLevelChange: (level) => {
        const numeric = Number(level);
        setVoiceLevel(Number.isFinite(numeric) ? Math.max(0, Math.min(1, numeric)) : 0);
      },
      onDebug:          ()   => setVoiceDiagnostics(engine?.diagnostics || null),
    });
    engineRef.current = engine;
    return () => {
      engine.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // [] intencional — engine é singleton por montagem do componente

  const setContinuousMode = useCallback((enabled) => {
    engineRef.current?.setContinuousMode(enabled);
    setContinuousModeState(enabled);
  }, []);

  // Referências estáveis — seguras em arrays de dependência
  const startPassive  = useCallback(() => engineRef.current?.startPassive(),  []);
  const startActive   = useCallback(() => engineRef.current?.startActive(),   []);
  const startManual   = useCallback(() => engineRef.current?.startManual(),   []);
  const toggleManual  = useCallback(() => engineRef.current?.toggleManual(),  []);
  const finishManual  = useCallback(() => engineRef.current?.finishManual(),  []);
  const finishActive  = useCallback(() => engineRef.current?.finishActive(),  []);
  const startSpeaking = useCallback(() => engineRef.current?.startSpeaking(), []);
  const doneSpeaking  = useCallback(() => engineRef.current?.doneSpeaking(),  []);
  const finishResponseWithoutSpeech = useCallback(() => engineRef.current?.finishResponseWithoutSpeech(), []);
  const cancelSpeaking = useCallback(() => engineRef.current?.cancelSpeaking?.(), []);
  const stop          = useCallback(() => engineRef.current?.stop(),          []);
  const mute          = useCallback(() => engineRef.current?.mute(),          []);
  const setProcessing = useCallback(() => engineRef.current?.setProcessing(), []);
  const setIdle       = useCallback(() => engineRef.current?.setIdle(),       []);
  const setFocusMode  = useCallback((v) => engineRef.current?.setFocusMode(v), []);
  const beginBargeIn  = useCallback(() => engineRef.current?.beginBargeIn?.(), []);
  const getVoiceDiagnostics = useCallback(() => engineRef.current?.diagnostics || null, []);
  const getVoiceCapabilities = useCallback(() => engineRef.current?.getCapabilities?.() || null, []);

  return {
    voiceState,
    continuousMode,
    voiceLevel,
    startPassive,
    startActive,
    startManual,
    toggleManual,
    finishManual,
    finishActive,
    startSpeaking,
    doneSpeaking,
    finishResponseWithoutSpeech,
    cancelSpeaking,
    stop,
    mute,
    setProcessing,
    setIdle,
    setContinuousMode,
    setFocusMode,
    beginBargeIn,
    voiceDiagnostics,
    getVoiceDiagnostics,
    getVoiceCapabilities,
  };
}
